import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { Box, makeStyles, Typography, Button, TextField, IconButton, SvgIconTypeMap } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClearIcon from '@material-ui/icons/Clear';
import SimpleTextInfo from '../common/SimpleTextInfo';
import JsonDisplay from '../common/JsonDisplay/JsonDisplay';
import JsonDisplayModal from '../common/JsonDisplay/JsonDisplayModal';
import { getRequest } from '../../utils/httpClient';

const useStyles = makeStyles((theme) => ({
	modalDataCardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: '1vh'
	},
	centralBox: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		maxHeight: '80vh',
		minHeight: '80vh'
	}
}));

export const AlignmentViewer: React.FC<AlignmentViewerProps> = (props) => {
	const { id, sport, open, setOpen, icon } = props;
	const classes = useStyles();
	const [pbpIdFilter, setPbpIdFilter] = useState(null);

	const {
		isLoading,
		data: alignmentData,
		isError,
		refetch,
		remove
	} = useQuery(['alignment', id], () => getRequest('sports-data', `/getLatestAlignment?airingId=${id}&sport=${sport}`), {
		enabled: false
	});

	useEffect(() => {
		if (open) refetch();
		else remove();
	}, [open, refetch, remove]);

	let filteredAlignment = alignmentData?.data ? JSON.parse(alignmentData.data) : null;
	if (pbpIdFilter && filteredAlignment) {
		filteredAlignment = {
			...filteredAlignment,
			playByPlay: filteredAlignment.playByPlay.filter((alignment) => {
				return alignment.id === pbpIdFilter;
			})
		};
	}

	const debouncedClick = useCallback(
		_.debounce(
			() => {
				refetch();
			},
			2000,
			{ leading: true, trailing: false, maxWait: 2000 }
		),
		[]
	);

	return (
		<JsonDisplayModal title={'Alignment Viewer'} setOpen={setOpen} open={open} icon={icon}>
			{isLoading && (
				<Box className={classes.centralBox}>
					<CircularProgress />
				</Box>
			)}

			{isError && (
				<Box className={classes.centralBox}>
					<Typography variant='subtitle2'>Error getting alignment</Typography>{' '}
				</Box>
			)}

			{alignmentData && (
				<>
					<Box className={classes.modalDataCardHeader}>
						<TextField
							size='small'
							id='id'
							label='Search PlayByPlay ID'
							name='id'
							value={pbpIdFilter ?? ''}
							onChange={(e): void => setPbpIdFilter(e.target.value)}
							variant='outlined'
							InputProps={{
								endAdornment: pbpIdFilter ? (
									<IconButton size='small' onClick={(): void => setPbpIdFilter(null)}>
										<ClearIcon />
									</IconButton>
								) : undefined
							}}
						/>
						<Button color='primary' onClick={(): void => debouncedClick()}>
							<RefreshIcon />
						</Button>
					</Box>
					<>
						<SimpleTextInfo label='File Name' value={alignmentData?.key || 'not specified'} />
						<SimpleTextInfo label='Last Modified' value={dayjs(alignmentData?.lastModified ?? '').format('DD/MM/YYYY HH:mm') || 'not specified'} />
						<SimpleTextInfo label='Data' value={''} />
						<JsonDisplay data={filteredAlignment} />
					</>
				</>
			)}
		</JsonDisplayModal>
	);
};

type AlignmentViewerProps = { id: string; sport: string; open: boolean; setOpen: (newValue: boolean) => boolean; icon: SvgIconTypeMap };

export default AlignmentViewer;
